import React, { useState } from "react";
import { partyTheme } from "./accountDetailTheme";

/** Chevron for styled native select (matches umber / chrome tone). */
const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='none' stroke='%235c5346' stroke-width='1.2' d='M2 3.5L5 6.5L8 3.5'/%3E%3C/svg%3E\")";

/**
 * Sits under the account header: pick which competitor portfolio claims
 * comparisons run against. Patent lists stay in the sidebar.
 *
 * New portfolio: inline form (not window.prompt). For a single required field
 * this keeps context in the strip; if we later add several fields or steps,
 * a modal or drawer would be reasonable.
 */
function AccountComparisonBar({
  competitorPortfolios,
  selectedCompPortfolioId,
  onSelectCompetitorPortfolio,
  onCreateCompetitorPortfolio,
}) {
  const hasCompetitors = competitorPortfolios.length > 0;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectStyle = {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "30px",
    padding: "6px 32px 6px 10px",
    border: `1px solid ${partyTheme.chrome.panelBorder}`,
    borderRadius: 0,
    fontSize: "12px",
    lineHeight: 1.25,
    fontFamily: "inherit",
    fontWeight: 500,
    color: partyTheme.chrome.heading,
    backgroundColor: hasCompetitors ? "#fff" : "#f0eeeb",
    cursor: hasCompetitors ? "pointer" : "not-allowed",
    WebkitAppearance: "none",
    MozAppearance: "none",
    appearance: "none",
    backgroundImage: hasCompetitors ? SELECT_CHEVRON : "none",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    outline: "none",
  };

  const secondaryButtonStyle = {
    boxSizing: "border-box",
    minHeight: "30px",
    padding: "6px 14px",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderRadius: 0,
    border: `1px solid ${partyTheme.theirs.border}`,
    background: "#fff",
    color: partyTheme.theirs.textLabel,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    outline: "none",
  };

  const primaryButtonStyle = {
    ...secondaryButtonStyle,
    background: partyTheme.yours.button,
    color: "#fff",
    border: `1px solid ${partyTheme.yours.buttonBorder}`,
    cursor: submitting ? "wait" : "pointer",
    opacity: submitting ? 0.85 : 1,
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setNewName("");
  };

  const submitCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onCreateCompetitorPortfolio(trimmed);
      if (ok) cancelCreate();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${partyTheme.chrome.neutralRule}`,
        background: partyTheme.theirs.bg,
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: "8px 12px",
          padding: "10px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "2px",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: partyTheme.theirs.textLabel,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              lineHeight: 1.2,
            }}
          >
            Competitor portfolio
          </span>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 400,
              color: "#7a756c",
              lineHeight: 1.25,
              maxWidth: "220px",
            }}
          >
            Compared against your default portfolio in claims
          </span>
        </div>

        <div
          style={{
            flex: "1 1 200px",
            minWidth: "min(100%, 200px)",
            maxWidth: "480px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <select
            value={hasCompetitors ? selectedCompPortfolioId : ""}
            onChange={(e) => onSelectCompetitorPortfolio(e.target.value)}
            disabled={!hasCompetitors}
            aria-label="Competitor portfolio"
            style={selectStyle}
          >
            {!hasCompetitors ? (
              <option value="">No competitor portfolios</option>
            ) : (
              competitorPortfolios.map((portfolio) => (
                <option
                  key={portfolio.portfolio_id}
                  value={portfolio.portfolio_id}
                >
                  {portfolio.portfolio_name}
                </option>
              ))
            )}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {!showCreateForm ? (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              style={{
                ...secondaryButtonStyle,
                textTransform: "none",
                letterSpacing: "0.02em",
              }}
            >
              Create competitor portfolio
            </button>
          ) : (
            <button
              type="button"
              onClick={cancelCreate}
              disabled={submitting}
              style={{
                ...secondaryButtonStyle,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {showCreateForm && (
        <div
          style={{
            padding: "0 16px 12px",
            borderTop: `1px solid ${partyTheme.chrome.panelBorder}`,
            marginTop: "0",
            paddingTop: "10px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px",
            background: "rgba(255,255,255,0.45)",
          }}
        >
          <label
            htmlFor="new-competitor-portfolio-name"
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color: partyTheme.theirs.text,
              flexShrink: 0,
            }}
          >
            Portfolio name
          </label>
          <input
            id="new-competitor-portfolio-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitCreate();
              }
              if (e.key === "Escape") cancelCreate();
            }}
            disabled={submitting}
            placeholder="e.g. Acme competitor set"
            autoFocus
            style={{
              flex: "1 1 180px",
              minWidth: "140px",
              maxWidth: "360px",
              boxSizing: "border-box",
              minHeight: "30px",
              padding: "6px 10px",
              border: `1px solid ${partyTheme.chrome.panelBorder}`,
              borderRadius: 0,
              fontSize: "12px",
              fontFamily: "inherit",
              lineHeight: 1.25,
              background: "#fff",
              color: partyTheme.chrome.heading,
              outline: "none",
            }}
          />
          <button
            type="button"
            onClick={submitCreate}
            disabled={submitting || !newName.trim()}
            style={primaryButtonStyle}
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountComparisonBar;
